import React, { useEffect, useMemo, useState } from "react";

import {
	Autocomplete,
	Box,
	Button,
	Chip,
	Grid,
	MenuItem,
	Paper,
	TextField,
	Typography,
	Tooltip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import LineChartComponent from "../../components/charts/LineChart";
import { Switch } from "@mui/material";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";

import { setInPageLoading, setIsLoading } from "../../store/LoadingSlice";
import { RootState } from "../../store/Store";
import {
	FetchDataFailureAction,
	FetchDataSuccessAction,
	fetchData,
	postData,
} from "../../store/actions";

import {
	addAttendanceRecord,
	listAttendanceRecords,
	getAttendanceChartData,
	viewActiveUsers,
} from "../../Service/Apis";

import dayjs, { Dayjs } from "dayjs";
import { formatDateConsistent, STANDARD_DATE_FORMAT } from "../../utils/dateUtils";
import NothingToDisplayComponent from "../../components/Data/NothingToShowComponent";
import {
	ToastVariant,
	useCustomToast,
} from "../../components/Methods/SnackBar";
import {
	AttendanceResponseType,
	AttendanceSummaryType,
	AttendanceType,
	CursorHistoryState,
} from "./Types/AttendaceTypes";
import { DataGrid, GridColDef, GridToolbar, GridToolbarContainer, GridToolbarQuickFilter } from "@mui/x-data-grid";
import CustomNoRowsOverlay from "../../components/Data/NoRowsOverlayComponent";
import AttendanceImportDialog from "./AttendanceImportDialog";
import Loading from "../../components/Loading/Loading";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
// import WeekSelector from "../../components/DatePickers/WeekSelector";

interface Employeetype {
	id: string;
	employee_code: string;
	first_name: string;
	last_name: string;
	gender: string;
	roles: [];
	designation: string;
	date_of_birth: string;
	date_of_joining: string;
	date_of_confirmation: string;
	date_of_exit: string;
	email: string;
	password: string;
}

interface PaginationState {
	cursor: string | null;
	hasNext: boolean;
	hasPrevious: boolean;
	count: number;
	distributionInfo?: {
		total_users: number;
		total_users_requested: number;
		users_with_data: number;
		users_without_data: number;
		records_per_user: Record<string, number>;
		balanced_distribution: boolean;
		distribution_quality: 'good' | 'partial';
	};
	history: CursorHistoryState;
}

/**
 * ReviewAttendance component for managing and displaying employee attendance records.
 *
 * @component
 * @description
 * This component provides functionality to:
 * - Select employees from a dropdown
 * - Filter attendance records by date range
 * - Display attendance data with cursor pagination
 * - Show attendance summary statistics
 * - Handle attendance record updates
 *
 * @features
 * - Employee selection via Autocomplete (multi-select)
 * - Date range filtering with DatePicker
 * - Cursor-based pagination with balanced distribution
 * - Attendance data grid with sortable columns
 * - Summary statistics display
 * - Loading states management
 *
 * @state
 * - employeesData: Array of employee data
 * - selectedEmployees: Currently selected employees (multi-select)
 * - startDate/endDate: Date range for filtering
 * - attendance: Current attendance records and summary
 * - pagination: Cursor pagination state (cursor, hasNext, hasPrevious, count)
 *
 * @hooks
 * - useDispatch: For Redux actions dispatch
 * - useSelector: For accessing loading state
 * - useCustomToast: For displaying notifications
 * - useEffect: For data fetching and side effects
 *
 * @returns {JSX.Element} A React component for reviewing and managing attendance records
 */
const ReviewAttendance = () => {
	// Default summary object
	const defaultSummary: AttendanceSummaryType = {
		total_late_marks: 0,
		total_overtime: 0,
		total_regularized_days: 0,
		avg_late_marks: 0,
		avg_overtime: 0,
		total_records: 0,
		total_leaves: 0,
		wfh_count: 0,
		total_wfh: 0,
		total_working_hours: 0,
		date_range: {
			start_date: null,
			end_date: null,
		},
		message: '',
		user_roles: [],
	};

	// State Hooks
	const [employeesData, setEmployeesData] = useState<Employeetype[]>([]);
	const [selectedEmployees, setSelectedEmployees] = useState<Employeetype[]>([]);
	const [selectedEmployeesChart, setSelectedEmployeesChart] = useState<string[]>([]);
	const [selectAll, setSelectAll] = useState(false);
	const [startDate, setStartDate] = useState<Dayjs | null>(null);
	const [endDate, setEndDate] = useState<Dayjs | null>(null);
	const [attendance, setAttendance] = useState<AttendanceResponseType>({
		results: [],
		summary: defaultSummary,
	});
	const [chartData, setChartData] = useState<any[]>([]);
	const [forceUpdateKey, setForceUpdateKey] = useState(0);
	const [importDialogOpen, setImportDialogOpen] = useState(false);
	const [pagination, setPagination] = useState<PaginationState>({
		cursor: null,
		hasNext: false,
		hasPrevious: false,
		count: 0,
		history: {
			cursors: [null],           // Start with first page (null cursor)
			currentIndex: 0,           // Currently on page 1 (index 0)
			maxVisitedIndex: 0,        // Haven't gone beyond page 1
			totalPages: null,          // Don't know total pages yet
			isNavigating: false        // Not currently navigating
		}
	});
	const [chartLoaded, setChartLoaded] = useState(false);

	// Get user role from localStorage
	const userRole = localStorage.getItem("nRole");
	const isHROrAdmin = userRole
		? userRole.toLowerCase() === "hr" || userRole.toLowerCase() === "admin"
		: false;
	// Redux Hooks
	const dispatch = useDispatch();
	const dispatch2: ThunkDispatch<
		RootState,
		null,
		FetchDataSuccessAction | FetchDataFailureAction
	> = useDispatch();

	const showToast = useCustomToast();

	const isLoading = useSelector((state: any) => state.loading.isLoading);

	const handleShowToast = (
		message: string,
		variant: ToastVariant,
		isCloseable: boolean
	) => {
		showToast(message, variant, isCloseable);
	};

	// Custom toolbar component for DataGrid with direct CSV export
	const CustomToolbar = () => {
		const handleDirectExport = async () => {
			try {
				dispatch(setInPageLoading(true));
				
				// Prepare export API call with same filters but export=true
				const queryParams = new URLSearchParams();
				queryParams.append("export", "true");
				
				// Add same filters as current view
				selectedEmployees.forEach((employee: Employeetype) => {
					queryParams.append("user_id", employee.id);
				});
				
				if (startDate && endDate) {
					queryParams.append("date_from", startDate.format("YYYY-MM-DD"));
					queryParams.append("date_to", endDate.format("YYYY-MM-DD"));
				}

				const url = `${listAttendanceRecords}?${queryParams.toString()}`;
				// console.log("🔍 Fetching all data for export:", { url });

				// Fetching all data for export
				const response = await dispatch2(fetchData(url));
				const allData = Array.isArray(response.results) ? response.results : [];
				
				// Convert to CSV format
				const csvData = allData.map((row: any, index: number) => ({
					'Sr. No.': index + 1,
					'Employee Name': row.employee_name,
					'Month-Year': dayjs(row.date).format("MMM-YYYY"),
					'Date': formatDateConsistent(row.date),
					'Working Hours': row.hours_worked || 0,
					'Work from Home': row.status === "Work From Home" ? "Yes" : "No",
					'Leaves': row.total_leaves || 0,
					'Regularization': row.regularised ? "Yes" : "No"
				}));

				// Create CSV content
				const headers = Object.keys(csvData[0] || {});
				const csvContent = [
					headers.join(','),
					...csvData.map((row: any) => headers.map(header => `"${row[header] || ''}"`).join(','))
				].join('\n');

				// Create and trigger download
				const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
				const link = document.createElement('a');
				const downloadUrl = URL.createObjectURL(blob);
				link.setAttribute('href', downloadUrl);
				link.setAttribute('download', `attendance-records-all-${dayjs().format('YYYY-MM-DD')}.csv`);
				link.style.visibility = 'hidden';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				
				// console.log(`📊 Exported ${allData.length} records to CSV`);
				handleShowToast(`Successfully exported ${allData.length} records`, "success", true);
				
			} catch (error) {
				console.error("❌ Error exporting data:", error);
				handleShowToast("Error exporting data", "error", true);
			} finally {
				dispatch(setInPageLoading(false));
			}
		};

		return (
			<GridToolbarContainer sx={{ margin: "0.5rem" }}>
				<GridToolbarQuickFilter />
				<Button
					variant="outlined"
					size="small"
					startIcon={<CloudUploadIcon />}
					onClick={handleDirectExport}
					sx={{ ml: 1 }}
				>
					Export CSV
				</Button>
			</GridToolbarContainer>
		);
	};
	//Handle Employee Change Function
	const handleEmployeeChange = (_e: any, values: Employeetype[]) => {
		setSelectedEmployees(values);
	};
	// Add new type for chart metrics
	type ChartMetric = {
		value: string;
		label: string;
		accessor: keyof (typeof attendance.results)[0];
	};

	// Add new state for selected metric - Set Working Hours as default
	const [selectedMetric, setSelectedMetric] = useState<ChartMetric>({
		value: "hours_worked",
		label: "Working Hours",
		accessor: "hours_worked",
	});

	// Add array of available metrics for daily data
	const chartMetrics: ChartMetric[] = [
		{
			value: "hours_worked",
			label: "Working Hours",
			accessor: "hours_worked",
		},
		{
			value: "work_from_home_count",
			label: "Work from Home",
			accessor: "work_from_home_count",
		},
		{
			value: "total_leaves",
			label: "Leaves",
			accessor: "total_leaves",
		},
		{
			value: "regularised_days",
			label: "Regularization",
			accessor: "regularised_days",
		},
	];

	// Update transformDataForChart function to show daily data
	const transformDataForChart = (data: any[]) => {
		return data
			.slice()
			.sort(
				(a: any, b: any) =>
					dayjs(a.date).unix() - dayjs(b.date).unix()
			)
			.map((item: any) => ({
				week: formatDateConsistent(item.date),
				tooltip: {
					start: formatDateConsistent(item.date),
					end: formatDateConsistent(item.date),
				},
				employee_name: item.employee_name,
				percent: item[selectedMetric.accessor],
				employee_id: item.user_id,
			}));
	};

	// Filter chart data by selected date range
	const getFilteredChartData = (data: any[]) => {
		// If no date range is selected, return all data (for default behavior)
		if (!startDate || !endDate) {
			return transformDataForChart(data);
		}

		// Filter data to only include records within the selected date range
		const filtered = data.filter((item: any) => {
			const itemDate = dayjs(item.date);
			const isAfterStart = itemDate.isAfter(startDate) || 
									itemDate.isSame(startDate, 'day');
			const isBeforeEnd = itemDate.isBefore(endDate) || 
								   itemDate.isSame(endDate, 'day');
			return isAfterStart && isBeforeEnd;
		});

		return transformDataForChart(filtered);
	};

	// Add handler for employee selection in chart
	const handleEmployeeSelection = (employeeName: string) => {
		setSelectedEmployeesChart((prev) => {
			if (prev.includes(employeeName)) {
				return prev.filter((name) => name !== employeeName);
			}
			return [...prev, employeeName];
		});
	};

	// Add handler for select all switch
	const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const checked = event.target.checked;
		setSelectAll(checked);
		if (checked) {
			setSelectedEmployeesChart(
				Array.isArray(attendance.results) 
					? attendance.results.map((item) => item.employee_name)
					: []
			);
		} else {
			setSelectedEmployeesChart([]);
		}
	};
	const rows = Array.isArray(attendance.results)
		? attendance.results.map((data, index) => ({
			...data,
			index: index,
			sr_no: index + 1,
			month_year: dayjs(data.date).format("MMM-YYYY"),
		}))
		: [];

	const columns: GridColDef[] = useMemo(() => {
		return [
			{
				field: "sr_no",
				headerName: "Sr. No.",
				flex: 0.5,
				align: "center",
				headerAlign: "center",
				headerClassName: "header-background",
				renderHeader: () => (
					<strong className="custom-header" style={{ fontSize: 15 }}>
						Sr. No.
					</strong>
				),
			},
			{
				field: "employee_name",
				headerName: "Employee Name",
				align: "center",
				flex: 1,
				headerAlign: "center",
				headerClassName: "header-background",
				renderHeader: () => (
					<strong className="custom-header" style={{ fontSize: 15 }}>
						Employee Name
					</strong>
				),
			},
			{
				field: "month_year",
				headerName: "Month-Year",
				align: "center",
				flex: 0.9,
				headerAlign: "center",
				headerClassName: "header-background",
				renderHeader: () => (
					<strong className="custom-header" style={{ fontSize: 15 }}>
						Month-Year
					</strong>
				),
			},

			{
				field: "date",
				headerName: "Date",
				align: "center",
				flex: 1,
				headerAlign: "center",
				headerClassName: "header-background",
				renderCell: (params) =>
					params?.value
						? formatDateConsistent(params.value)
						: "-",
				renderHeader: () => (
					<strong className="custom-header" style={{ fontSize: 15 }}>
						Date
					</strong>
				),
			},
			{
				field: "hours_worked",
				headerName: "Working Hours",
				align: "center",
				flex: 1,
				headerAlign: "center",
				headerClassName: "header-background",
				renderHeader: () => (
					<strong className="custom-header" style={{ fontSize: 15 }}>
						Working Hours
					</strong>
				),
			},
			{
				field: "work_from_home_count",
				headerName: "Work from Home",
				align: "center",
				flex: 1,
				headerAlign: "center",
				headerClassName: "header-background",
				renderHeader: () => (
					<strong className="custom-header" style={{ fontSize: 15 }}>
						Work from Home
					</strong>
				),
				valueGetter: (params) => {
					// Count WFH days based on status
					return params.row.status === "Work From Home" ? 1 : 0;
				},
				renderCell: (params) => (
					<Chip
						label={params.value === 1 ? "Yes" : "No"}
						color={params.value === 1 ? "info" : "default"}
						variant="outlined"
						size="small"
					/>
				),
			},
			{
				field: "total_leaves",
				headerName: "Leaves",
				align: "center",
				flex: 0.8,
				headerAlign: "center",
				headerClassName: "header-background",
				renderHeader: () => (
					<strong className="custom-header" style={{ fontSize: 15 }}>
						Leaves
					</strong>
				),
			},
			{
				field: "regularised_days",
				headerName: "Regularization",
				align: "center",
				flex: 1,
				headerAlign: "center",
				headerClassName: "header-background",
				renderHeader: () => (
					<strong className="custom-header" style={{ fontSize: 15 }}>
						Regularization
					</strong>
				),
				valueGetter: (params) => {
					// Use the new regularised boolean field if available, otherwise fall back to regularized_days
					if (params.row.regularised !== undefined) {
						return params.row.regularised ? 1 : 0;
					}
					return params.row.regularized_days || 0;
				},
				renderCell: (params) => (
					<Chip
						label={params.value > 0 ? "Yes" : "No"}
						color={params.value > 0 ? "warning" : "default"}
						variant="outlined"
						size="small"
					/>
				),
			},
		];
	}, [rows]);

	const SummaryCard = ({ title, value, icon: Icon }: { title: string; value: string | number; icon?: any }) => {
		return (
			<Paper sx={{
				p: 2,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				backgroundColor: '#f5f5f5',
				borderRadius: 2,
				boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
				minHeight: 120,
				transition: 'transform 0.2s, boxShadow 0.2s',
				'&:hover': {
					transform: 'translateY(-4px)',
					boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
				}
			}}>
				{Icon && <Box sx={{ fontSize: 32, color: '#007300', mb: 1 }}>{Icon}</Box>}
				<Typography variant="caption" sx={{ color: '#666', mb: 1 }}>
					{title}
				</Typography>
				<Typography variant="h5" sx={{ fontWeight: 700, color: '#007300' }}>
					{value}
				</Typography>
			</Paper>
		);
	};

	const AttendanceSummary = ({ summary }: { summary: any }) => {
		// Helper function to safely format numbers
		const formatNumber = (
			value: number | null | undefined,
			decimals: number = 2
		): string => {
			if (value === null || value === undefined || isNaN(value)) return "0";
			return Number(value).toFixed(decimals);
		};

		return (
			<Box sx={{ mt: 2, mb: 4 }}>
				<Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
					Summary
				</Typography>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={3}>
						<SummaryCard
							title="Total Regularized Days"
							value={formatNumber(summary?.total_regularized_days, 0)}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<SummaryCard
							title="Total Leaves"
							value={formatNumber(summary?.total_leaves, 0)}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<SummaryCard
							title="Work from Home"
							value={formatNumber(summary?.wfh_count || summary?.total_wfh, 0)}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<SummaryCard
							title="Total Records"
							value={formatNumber(summary?.total_records, 0)}
						/>
					</Grid>
				</Grid>
			</Box>
		);
	};

	// Add TypeScript interface if not already defined

	useEffect(() => {
		// Set default date range to last 7 days available in the table on component mount
		if (startDate === null && endDate === null) {
			// We'll set this after we get the data from the API
			// console.log("📅 Will set default date range after data is loaded");
		}
	}, []);

	useEffect(() => {
		// API Call get the User Details.
		const fetchUserDetails = async () => {
			try {
				dispatch(setIsLoading(true));
				const response = await dispatch2(fetchData(`${viewActiveUsers}`));
				// console.log("👥 Active users fetched:", {
				// 	count: response?.length || 0,
				// 	users: response
				// });
				setEmployeesData(response as Employeetype[]);
			} catch (error) {
				console.error("❌ Error fetching users:", error);
			} finally {
				dispatch(setIsLoading(false));
			}
		};

		// Log current user info
		const userRole = localStorage.getItem("nRole");
		const userId = localStorage.getItem("nUserId");
		// console.log("👤 Current user info:", {
		// 	role: userRole,
		// 	userId: userId,
		// 	isHROrAdmin: userRole ? userRole.toLowerCase() === "hr" || userRole.toLowerCase() === "admin" : false
		// });

		fetchUserDetails();
	}, []);

	// Fetch chart data using dedicated chart endpoint for better performance and complete data
	const fetchChartDataForVisual = async () => {
		try {
			const queryParams = new URLSearchParams();
			
			// Add multiple user IDs for chart data
			selectedEmployees.forEach(employee => {
				queryParams.append("user_id", employee.id);
			});
			
			// Add date filters
			if (startDate && endDate) {
				queryParams.append("date_from", startDate.format("YYYY-MM-DD"));
				queryParams.append("date_to", endDate.format("YYYY-MM-DD"));
			}
			
			// Set reasonable limits for chart performance
			queryParams.append("max_records", "5000"); 
			queryParams.append("sampling", "none"); 
			
			const url = `${getAttendanceChartData}?${queryParams.toString()}`;
			const response = await dispatch2(fetchData(url));
			
			// Set chart data with all results
			setChartData(Array.isArray(response.results) ? response.results : []);
			
			// Log chart data info for debugging
			console.log(`📊 Chart data loaded: ${response.results?.length || 0} records for ${selectedEmployees.length} employees`);
			if (response.sampling_applied) {
				console.log(`⚠️ Sampling applied: ${response.sampling_method}, total available: ${response.total_available}`);
			}
		} catch (error) {
			console.error("❌ Error fetching chart data:", error);
			setChartData([]);
		}
	};

	const fetchAttendance = async (cursor: string | null = null, updateHistory: boolean = true) => {
		try {
			// Prevent concurrent navigation
			// if (pagination.history.isNavigating) {
			// 	console.log("Navigation already in progress, skipping...");
			// 	return;
			// }

			dispatch(setInPageLoading(true));
			
			// Set navigating flag
			if (updateHistory) {
				setPagination(prev => ({
					...prev,
					history: { ...prev.history, isNavigating: true }
				}));
			}
			
			const queryParams = new URLSearchParams();
			
			// Add multiple user IDs for balanced distribution
			selectedEmployees.forEach(employee => {
				queryParams.append("user_id", employee.id);
			});
			
			// Add cursor for pagination
			if (cursor) {
				queryParams.append("cursor", cursor);
			}
			
			// Add date filters
			if (startDate && endDate) {
				queryParams.append("date_from", startDate.format("YYYY-MM-DD"));
				queryParams.append("date_to", endDate.format("YYYY-MM-DD"));
			}

			const url = `${listAttendanceRecords}?${queryParams.toString()}`;
			// console.log("🔍 Fetching attendance with cursor pagination:", {
			// url,
			// 	selectedEmployees: selectedEmployees.length,
			// 	cursor: cursor ? "present" : "initial",
			// 	hasDateFilters: !!(startDate && endDate),
			// updateHistory
			// });

			const response = await dispatch2(fetchData(url));
			
			// Direct assignment with cursor pagination data
			setAttendance({
				results: Array.isArray(response.results) ? response.results : [],
				summary: response.summary || defaultSummary,
			});
			
			// Update pagination state with cursor history management
			setPagination(prev => {
				const newState = {
					cursor: response.next ? extractCursorFromUrl(response.next) : null,
					hasNext: !!response.next,
					hasPrevious: prev.history.currentIndex > 0, // Can go back if not on first page
					count: response.count || 0,
					distributionInfo: response.distribution_info,
					history: { ...prev.history, isNavigating: false }
				};

				// Update cursor history only if this is a new navigation (not a back/forward)
				if (updateHistory) {
					const nextCursor = response.next ? extractCursorFromUrl(response.next) : null;
					
					// Determine if we're moving forward or resetting
					if (cursor === null) {
						// Reset to first page
						newState.history = {
							cursors: [null],
							currentIndex: 0,
							maxVisitedIndex: 0,
							totalPages: !response.next ? 1 : null, // If no next, we know total is maxvisitedindex
							isNavigating: false
						};
					} else {
						// Moving forward to next page
						const nextIndex = prev.history.currentIndex + 1;
						const newCursors = [...prev.history.cursors];
						
						// Add next cursor if we haven't been to this page before
						if (nextIndex >= newCursors.length && nextCursor) {
							newCursors.push(nextCursor);
						}
						
						newState.history = {
							cursors: newCursors,
							currentIndex: nextIndex,
							maxVisitedIndex: Math.max(prev.history.maxVisitedIndex, nextIndex),
							totalPages: !response.next ? nextIndex + 1 : prev.history.totalPages,
							isNavigating: false
						};
					}
				}

				return newState;
			});

			// console.log("📊 Cursor Pagination Response:", {
			//  totalRecords: response.results?.length || 0,
			//  hasNext: !!response.next,
			//  distributionInfo: response.distribution_info,
			// currentPage: pagination.history.currentIndex + 1,
			// updateHistory
			// });
			
		} catch (error) {
			console.error("❌ Error fetching attendance:", error);
			handleShowToast("Error fetching attendance records", "error", true);
			setAttendance({
				results: [],
				summary: defaultSummary,
			});
			
			// Reset navigating flag on error
			setPagination(prev => ({
				...prev,
				history: { ...prev.history, isNavigating: false }
			}));
		} finally {
			dispatch(setInPageLoading(false));
		}
	};

	// Helper function to extract cursor from URL
	const extractCursorFromUrl = (url: string): string | null => {
		try {
			const urlObj = new URL(url);
			return urlObj.searchParams.get('cursor');
		} catch {
			return null;
		}
	};

	// Navigation helper functions

	const handleNext = () => {
		if (!pagination.hasNext || pagination.history.isNavigating) return;
		
		// Move forward to next page (this will update history)
		fetchAttendance(pagination.cursor, true);
	};

	const handlePrevious = () => {
		if (pagination.history.currentIndex <= 0 || pagination.history.isNavigating) return;
		
		const prevIndex = pagination.history.currentIndex - 1;
		const targetCursor = pagination.history.cursors[prevIndex];
		
		// Update current index without updating history (since we're going back to existing page)
		setPagination(prev => ({
			...prev,
			history: {
				...prev.history,
				currentIndex: prevIndex
			}
		}));
		
		// Fetch data for previous page without updating history
		fetchAttendance(targetCursor, false);
	};

	const handleFirst = () => {
		if (pagination.history.currentIndex === 0 || pagination.history.isNavigating) return;
		
		// Update current index to first page
		setPagination(prev => ({
			...prev,
			history: {
				...prev.history,
				currentIndex: 0
			}
		}));
		
		// Fetch data for first page (cursor = null) without updating history
		fetchAttendance(null, false);
	};

	//  Cursor Pagination Controls Component with history (simplified)
	const CursorPaginationControls = () => {
		const currentPage = pagination.history.currentIndex + 1;
		const totalPages = pagination.history.totalPages;

		return (
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, p: 2 }}>
				{/* Left Side - Navigation Buttons */}
					<Box sx={{ display: 'flex', gap: 1 }}>
						<Button
							variant="outlined"
							disabled={pagination.history.currentIndex === 0 || pagination.history.isNavigating} // disabled if we are on first page
							onClick={handleFirst}
							size="small"
						>
							First Page
						</Button>
						<Button
							variant="outlined"
							disabled={pagination.history.currentIndex === 0 || pagination.history.isNavigating} // disabled if we are on first page
							onClick={handlePrevious}
							size="small"
						>
							Previous
						</Button>
						<Button
							variant="outlined"
							disabled={!pagination.hasNext || pagination.history.isNavigating}
							onClick={handleNext}
							size="small"
						>
							Next Page
						</Button>
					</Box>
					
					{/* Center - Page Info and Records Info */}
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
						<Typography variant="body2" color="text.secondary">
							Page {currentPage} {totalPages ? `of ${totalPages}` : ''}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Showing {attendance.results.length} records
							{pagination.distributionInfo && (
								<>
									<Chip 
										label={`${pagination.distributionInfo.users_with_data}/${pagination.distributionInfo.total_users_requested} employees`}
										size="small"
										variant="outlined"
										color={pagination.distributionInfo.distribution_quality === 'good' ? 'success' : 'warning'}
										sx={{ ml: 1 }}
										title={`${pagination.distributionInfo.users_with_data} employees have data, ${pagination.distributionInfo.users_without_data} have no records in this range`}
									/>
									{pagination.distributionInfo.users_without_data > 0 && (
										<Chip 
											label={`${pagination.distributionInfo.users_without_data} no data`}
											size="small"
											variant="outlined"
											color="warning"
											sx={{ ml: 0.5 }}
										/>
									)}
								</>
							)}
						</Typography>
					</Box>
					
					{/* Right Side - Page Status */}
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						{pagination.history.isNavigating && (
							<Typography variant="caption" color="primary">
								Loading...
							</Typography>
						)}
						{pagination.hasNext && (
							<Typography variant="caption" color="text.secondary">
								More pages available
							</Typography>
						)}
					</Box>
			</Box>
		);
	};

	useEffect(() => {
		// API call to get the employee attendance records.
		// Always fetch data on initial load, then when dependencies change
		// console.log("🔄 useEffect triggered for fetchAttendance:", {
		// 	startDate: startDate?.format('YYYY-MM-DD'),
		// 	endDate: endDate?.format('YYYY-MM-DD'),
		// 	selectedEmployees: selectedEmployees.map(emp => `${emp.first_name} ${emp.last_name}`),
		// 	forceUpdateKey
		// });

		// Reset pagination state when filters change
		setPagination(prev => ({
			...prev,
			cursor: null,
			hasNext: false,
			hasPrevious: false,
			count: 0,
			distributionInfo: undefined
		}));

		// Show loading message for filter changes
		// if (startDate || endDate || selectedEmployees.length > 0) {
			// console.log("✅ Filters changed - resetting pagination and fetching fresh data");
		// }

		// console.log("✅ Calling fetchAttendance with cursor pagination");
		fetchAttendance(null); // Start with no cursor (first page)
		fetchChartDataForVisual(); 
	}, [selectedEmployees, startDate, endDate, forceUpdateKey]);

	const handleImportSuccess = () => {
		// Refresh the attendance data after successful import
		setForceUpdateKey((prev) => prev + 1);
	};

	// Set default select all employees when data loads
	useEffect(() => {
		if (Array.isArray(attendance.results) && attendance.results.length > 0) {
			const allEmployeeNames = attendance.results.map((item) => item.employee_name);
			setSelectedEmployeesChart(allEmployeeNames);
			setSelectAll(true);
			// Set chart as loaded when data is available
			setChartLoaded(true);

			// Set default date range to last 7 days available in the table if not already set
			if (!startDate && !endDate) {
				const sortedDates = attendance.results
					.map((item: any) => dayjs(item.date))
					.sort((a: dayjs.Dayjs, b: dayjs.Dayjs) => b.unix() - a.unix()); // Sort descending (newest first)
				
				if (sortedDates.length > 0) {
					const latestDate = sortedDates[0];
					const sevenDaysEarlier = latestDate.subtract(6, 'days'); // 7 days total including the latest date
					
					setStartDate(sevenDaysEarlier);
					setEndDate(latestDate);
					// console.log("📅 Default 7-day date range set based on available data:", {
					// 	start: sevenDaysEarlier.format('YYYY-MM-DD'),
					// 	end: latestDate.format('YYYY-MM-DD')
					// });
				}
			}
		} else {
			setChartLoaded(false);
		}
	}, [attendance.results, startDate, endDate]);

	// Loading Spinner Component
	if (isLoading) {
		return (
			<>
				<Loading isLoading={isLoading as boolean} />
			</>
		);
	}

	// Main Component

	return (
		<>
			<Box sx={{ width: "100%" }}>
				
				{/* Summary Section - Cards displayed at the top */}
				{attendance.results.length > 0 && attendance.summary && (
					<Box sx={{ width: "100%" }}>
						<AttendanceSummary summary={attendance.summary} />
					</Box>
				)}

				{/* Filters Section */}
				<Box sx={{ 
					display: "flex", 
					flexDirection: "column",
					gap: 2,
					mb: 4,
					p: 2,
					backgroundColor: "#fafafa",
					borderRadius: 2,
					width: "100%"
				}}>
						<Typography variant="h6" sx={{ fontWeight: 600 }}>
							Filters
						</Typography>
						
						<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
							{/* Left side - Import Button for HR/Admin */}
							<Box sx={{ display: "flex", alignItems: "flex-start" }}>
								{isHROrAdmin && (
									<Button
										variant="contained"
										startIcon={<CloudUploadIcon />}
										onClick={() => setImportDialogOpen(true)}
										size="small"
										sx={{
											backgroundColor: "#1976d2",
											"&:hover": {
												backgroundColor: "#1565c0",
											},
										}}
									>
										Import Attendance Data
									</Button>
								)}
							</Box>

							{/* Right side - Filters */}
							<Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
								{/* Employee Autocomplete - Multi-select */}
								<Autocomplete
									multiple
									size="small"
									isOptionEqualToValue={(option, value) => option.id === value.id}
									getOptionLabel={(option) =>
										option.first_name + " " + option.last_name
									}
									value={selectedEmployees}
									onChange={handleEmployeeChange}
									id="employee-autocomplete"
									options={employeesData}
									sx={{ width: "auto", minWidth: "250px", maxWidth: "400px" }}
									renderInput={(params) => <TextField {...params} label="Employee Name" />}
									renderTags={(value, getTagProps) =>
										value.map((option, index) => (
											<Chip
												variant="outlined"
												label={`${option.first_name} ${option.last_name}`}
												{...getTagProps({ index })}
												key={option.id}
											/>
										))
									}
								/>

								{/* Date Range Selectors */}
								<LocalizationProvider dateAdapter={AdapterDayjs}>
									<Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
										<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
											<Box sx={{ minWidth: "150px" }}>
												<DatePicker
													label="Start Date"
													format={STANDARD_DATE_FORMAT}
													value={startDate}
													onChange={(newValue) => {
														setStartDate(newValue);
														// Validate that end date is not before start date
														if (endDate && newValue && endDate.isBefore(newValue)) {
															setEndDate(newValue);
														}
													}}
													slotProps={{
														textField: {
															size: "small",
															fullWidth: true,
															placeholder: STANDARD_DATE_FORMAT,
															inputProps: {
																'aria-label': `Start date in ${STANDARD_DATE_FORMAT} format`
															}
														},
													}}
													disableFuture={true}
												/>
											</Box>
											<Box sx={{ minWidth: "150px" }}>
												<DatePicker
													label="End Date"
													format={STANDARD_DATE_FORMAT}
													value={endDate}
													onChange={(newValue) => {
														// Validate that end date is not before start date
														if (startDate && newValue && newValue.isBefore(startDate)) {
															handleShowToast("End date cannot be before start date", "error", true);
															return;
														}
														setEndDate(newValue);
													}}
													slotProps={{
														textField: {
															size: "small",
															fullWidth: true,
															placeholder: STANDARD_DATE_FORMAT,
															inputProps: {
																'aria-label': `End date in ${STANDARD_DATE_FORMAT} format`
															}
														},
													}}
													disableFuture={true}
												/>
											</Box>
										</Box>
										{/* Clear button below the date filters, right corner */}
										<Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
											<Button
												size="small"
												variant="outlined"
												onClick={() => {
													// Reset to default: last 7 days available in the table
													if (Array.isArray(attendance.results) && attendance.results.length > 0) {
														const sortedDates = attendance.results
															.map((item: any) => dayjs(item.date))
															.sort((a: dayjs.Dayjs, b: dayjs.Dayjs) => b.unix() - a.unix()); // Sort descending (newest first)
														
														if (sortedDates.length > 0) {
															const latestDate = sortedDates[0];
															const sevenDaysEarlier = latestDate.subtract(6, 'days'); // 7 days total including the latest date
															
															setStartDate(sevenDaysEarlier);
															setEndDate(latestDate);
														}
													} else {
														// If no data, clear the filters
														setStartDate(null);
														setEndDate(null);
													}
													setSelectedEmployees([]);
												}}
												sx={{ 
													color: "#d32f2f",
													borderColor: "#d32f2f",
													"&:hover": {
														backgroundColor: "#d32f2f",
														color: "white",
														borderColor: "#d32f2f",
													}
												}}
											>
												Clear
											</Button>
										</Box>
									</Box>
								</LocalizationProvider>
							</Box>
						</Box>

						{/* Commented Week Range Selectors */}
						{/* 
						<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
							<Box sx={{ minWidth: "200px" }}>
								<Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
									Start Week
								</Typography>
								<WeekSelector
									value={startWeekRange}
									onChange={(value) => {
										setStartWeekRange(value);
										// Validate that end week is not before start week
										if (endWeekRange.startDate && value.startDate && 
											endWeekRange.startDate.isBefore(value.startDate)) {
											setEndWeekRange(value);
										}
									}}
									disableFuture={true}
								/>
							</Box>
							<Box sx={{ minWidth: "200px" }}>
								<Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
									End Week
								</Typography>
								<WeekSelector
									value={endWeekRange}
									onChange={(value) => {
										// Validate that end week is not before start week
										if (startWeekRange.startDate && value.startDate && 
											value.startDate.isBefore(startWeekRange.startDate)) {
											handleShowToast("End week cannot be before start week", "error", true);
											return;
										}
										setEndWeekRange(value);
									}}
									disableFuture={true}
								/>
							</Box>
						</Box>
						*/}
					</Box>

				{/* No Results State */}
				{attendance.results.length === 0 ? (
					<Box
						sx={{
							mt: 12,
							display: "flex",
							justifyContent: "center",
							width: "100%",
						}}
					>
						<NothingToDisplayComponent />
					</Box>
				) : (
					<>
							{/* Daily Attendance Chart */}
							<Box sx={{ 
								mt: 4, 
								mb: 4, 
								p: 2, 
								backgroundColor: "white", 
								borderRadius: 2, 
								boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
								width: "100%",
								minHeight: "450px"
							}}>
								<Box
									display="flex"
									justifyContent="space-between"
									alignItems="center"
									mb={2}
									flexWrap="wrap"
									gap={2}
								>
									<Box display="flex" alignItems="center" gap={2}>
										<Typography variant="h6" sx={{ fontWeight: 600 }}>
											Daily Attendance Chart
										</Typography>
										<TextField
											select
											size="small"
											value={selectedMetric.value}
											onChange={(e) => {
												const metric = chartMetrics.find(
													(m) => m.value === e.target.value
												);
												if (metric) setSelectedMetric(metric);
											}}
											sx={{ minWidth: 150 }}
										>
											{chartMetrics.map((metric) => (
												<MenuItem
													key={metric.value}
													value={metric.value}
												>
													{metric.label}
												</MenuItem>
											))}
										</TextField>
									</Box>
									<Box display="flex" alignItems="center" gap={1}>
										<Typography>Select All</Typography>
										<Switch
											checked={selectAll}
											onChange={handleSwitchChange}
											color="primary"
										/>
									</Box>
								</Box>
								<LineChartComponent
									data={getFilteredChartData(chartData)}
									YAxisLabel={selectedMetric.label}
									selectedEmployees={selectedEmployeesChart}
									onEmployeeSelection={handleEmployeeSelection}
									hideWeeklyAverage={true}
								/>
							</Box>

							{/* Table Section - Only render after chart is loaded */}
							{chartLoaded && (
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										mt: 6,
										mb: 4,
									}}
								>
									<Box
										sx={{
											width: "100%",
											maxWidth: "1600px",
											mx: "auto",
											px: "16px",
										}}
									>
										<Paper sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
										<DataGrid
											autoHeight
											disableColumnMenu
											hideFooterPagination={true} // Hide default pagination
											sx={{
												width: "100%",
												height: "100%",
												"--DataGrid-overlayHeight": "50vh",
												"& .MuiDataGrid-root": {
													borderRadius: 1,
												},
											}}
											rows={rows}
											columns={columns}
											disableColumnSelector
											disableDensitySelector
											disableColumnFilter
											slots={{
												toolbar: CustomToolbar,
												noRowsOverlay: CustomNoRowsOverlay,
												noResultsOverlay: CustomNoRowsOverlay,
											}}
											getRowId={(row) => row.attendance_id}
										/>
										{/* Add cursor pagination controls */}
										<CursorPaginationControls />
									</Paper>
									
								</Box>
							</Box>
						)}
						</>
					)}
			</Box>
			{/* Import Dialog */}
			<AttendanceImportDialog
				open={importDialogOpen}
				onClose={() => setImportDialogOpen(false)}
				onSuccess={handleImportSuccess}
			/>
		</>
	);
};

export default ReviewAttendance;
